/**
 * MobileApprovals — Approvals Hub on mobile.
 * Same data source as desktop ApprovalsHub (/approvals/pending).
 * Touch-optimised: large tap targets, bottom sheet for reject reason,
 * category filter pills, 60s auto-refresh.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, XCircle, RefreshCw, Inbox, ChevronDown, AlertTriangle } from 'lucide-react';
import { apiGet, apiPost, apiPut, apiPatch } from '../../lib/api';
import { toast } from 'sonner';

const CATEGORY_COLORS = {
  Procurement: 'bg-teal-100 text-teal-700',
  HR:          'bg-violet-100 text-violet-700',
  Finance:     'bg-emerald-100 text-emerald-700',
  Safety:      'bg-red-100 text-red-700',
  Labour:      'bg-amber-100 text-amber-700',
  Admin:       'bg-slate-100 text-slate-600',
};

const TYPE_ICONS = {
  mpr:'📋', po:'🛒', work_order:'📝', vendor_application:'🏢',
  vendor_change:'🔄', leave:'🏖️', overtime:'⏰', regularization:'🕐',
  mrf:'👥', payment_voucher:'💳', receipt_voucher:'🧾', vendor_invoice:'📄',
  labour_payrun:'💰', client_bill_cert:'📑', petty_cash:'💵',
  permit_to_work:'⛑️', itp_approval:'✅',
  user_approval:'👤', travel_request:'✈️', travel_expense:'🧳',
  reimbursement:'💸', measurement_book:'📐',
  wo_variation:'🔄', marriage_benefit:'💍',
};

async function callAPI(method, endpoint, body = {}) {
  const m = (method || 'POST').toLowerCase();
  if (m === 'post')  return apiPost(endpoint, body);
  if (m === 'put')   return apiPut(endpoint, body);
  if (m === 'patch') return apiPatch(endpoint, body);
  return apiGet(endpoint);
}

// ── Reject bottom sheet ──────────────────────────────────────────────────────
function RejectSheet({ item, onConfirm, onCancel }) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-end" data-testid="m-reject-sheet">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-white w-full rounded-t-2xl p-5 pb-8">
        <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-4" />
        <h3 className="font-display font-bold text-[15px] mb-1">Reject</h3>
        <p className="text-xs text-slate-500 mb-3 truncate">{item.title}</p>
        <textarea
          autoFocus
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={3}
          placeholder="Reason for rejection…"
          className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400 resize-none"
          data-testid="m-reject-reason"
        />
        <div className="flex gap-2 mt-3">
          <button onClick={onCancel}
            className="flex-1 py-3 rounded-xl border border-slate-200 text-sm font-display font-bold text-slate-600">
            Cancel
          </button>
          <button
            disabled={!reason.trim()}
            onClick={() => onConfirm(reason)}
            className="flex-1 py-3 rounded-xl bg-rose-600 text-white text-sm font-display font-bold disabled:opacity-40"
            data-testid="m-reject-confirm">
            Confirm Reject
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Approval card ────────────────────────────────────────────────────────────
function ApprovalCard({ item, onAction }) {
  const [acting, setActing]           = useState(null);
  const [showSheet, setShowSheet]     = useState(false);
  const [expanded, setExpanded]       = useState(false);
  const [detailCache, setDetailCache] = useState({});  // id → extra_fields
  const [loadingDetail, setLoadingDetail] = useState(false);
  const catCls = CATEGORY_COLORS[item.category] || 'bg-slate-100 text-slate-600';

  const doApprove = async () => {
    if (!item.approve_endpoint) return;
    setActing('approve');
    try {
      await callAPI(item.approve_method, item.approve_endpoint);
      toast.success('Approved ✓');
      onAction(item.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not approve');
    } finally { setActing(null); }
  };

  const doReject = async (reason) => {
    if (!item.reject_endpoint) return;
    setShowSheet(false);
    setActing('reject');
    try {
      await callAPI(item.reject_method, item.reject_endpoint, reason ? { reason } : {});
      toast.success('Rejected');
      onAction(item.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reject');
    } finally { setActing(null); }
  };

  const handleToggle = async () => {
    const willExpand = !expanded;
    setExpanded(willExpand);
    if (willExpand && item.has_detail && !detailCache[item.id]) {
      setLoadingDetail(true);
      try {
        const r = await apiGet(`/approvals/${item.id}/detail?type=${item.type}`);
        setDetailCache(prev => ({ ...prev, [item.id]: r.extra_fields || {} }));
      } catch {
        // non-fatal — card still works without detail
      } finally { setLoadingDetail(false); }
    }
  };

  // Use cached detail; fall back to item.extra_fields for backward-compat
  const extraData = detailCache[item.id] || item.extra_fields || {};
  const extras = Object.entries(extraData).filter(([, v]) => v);

  return (
    <>
      {showSheet && (
        <RejectSheet item={item} onConfirm={doReject} onCancel={() => setShowSheet(false)} />
      )}
      <div
        className={`bg-white rounded-2xl border overflow-hidden ${
          item.urgency === 'high' ? 'border-rose-300' : 'border-slate-150'
        }`}
        data-testid={`m-approval-card-${item.id}`}
      >
        <div className="flex items-start gap-3 px-4 pt-4 pb-2" onClick={handleToggle}>
          <div className="text-2xl leading-none mt-0.5">{TYPE_ICONS[item.type] || '📌'}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              {item.urgency === 'high' && (
                <span className="flex items-center gap-0.5 text-[9px] font-bold uppercase text-rose-600 bg-rose-50 rounded-full px-1.5 py-0.5">
                  <AlertTriangle size={8} /> Urgent
                </span>
              )}
              <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${catCls}`}>
                {item.category}
              </span>
            </div>
            <p className="font-display font-bold text-[13px] text-slate-900 leading-snug">{item.title}</p>
            {item.subtitle && (
              <p className="text-[11px] text-slate-500 mt-0.5 truncate">{item.subtitle}</p>
            )}
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-[10px] text-slate-400">
              {item.project_name && <span>📁 {item.project_name}</span>}
              {item.raised_by && <span>👤 {item.raised_by}</span>}
              {item.amount > 0 && (
                <span className="font-semibold text-slate-600">
                  ₹{Number(item.amount).toLocaleString('en-IN')}
                </span>
              )}
            </div>
          </div>
          <ChevronDown size={14} className={`text-slate-300 mt-1 transition-transform shrink-0 ${expanded ? 'rotate-180' : ''}`} />
        </div>

        {expanded && (
          loadingDetail ? (
            <div className="px-4 pb-2 text-[11px] text-slate-400 flex items-center gap-1.5">
              <RefreshCw size={11} className="animate-spin" /> Loading details…
            </div>
          ) : extras.length > 0 && (
            <div className="px-4 pb-2 text-[11px] text-slate-500 space-y-0.5">
              {extras.map(([k, v]) => (
                <p key={k}><span className="text-slate-400">{k}: </span>{v}</p>
              ))}
            </div>
          )
        )}

        <div className="flex border-t border-slate-100 mt-1">
          {item.reject_endpoint && (
            <button
              disabled={!!acting}
              onClick={() => item.needs_reason_on_reject ? setShowSheet(true) : doReject('')}
              className="flex-1 flex items-center justify-center gap-1.5 py-3.5 text-[13px] font-display font-bold text-rose-600 active:bg-rose-50 transition-colors disabled:opacity-40"
              data-testid={`m-reject-btn-${item.id}`}
            >
              {acting === 'reject' ? <RefreshCw size={14} className="animate-spin" /> : <XCircle size={16} />}
              Reject
            </button>
          )}
          {item.approve_endpoint && (
            <button
              disabled={!!acting}
              onClick={doApprove}
              className="flex-1 flex items-center justify-center gap-1.5 py-3.5 text-[13px] font-display font-bold text-emerald-600 active:bg-emerald-50 transition-colors disabled:opacity-40 border-l border-slate-100"
              data-testid={`m-approve-btn-${item.id}`}
            >
              {acting === 'approve' ? <RefreshCw size={14} className="animate-spin" /> : <CheckCircle2 size={16} />}
              Approve
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const CATEGORIES = ['All', 'Procurement', 'HR', 'Finance', 'Safety', 'Labour', 'Admin'];

export default function MobileApprovals() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [cat, setCat]       = useState('All');

  const load = useCallback(async () => {
    try {
      const r = await apiGet('/approvals/pending');
      setData(r);
    } catch { toast.error('Could not load approvals'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const onAction = useCallback((id) => {
    setData(prev => prev ? {
      ...prev,
      items: prev.items.filter(i => i.id !== id),
      total: Math.max(0, (prev.total || 1) - 1),
    } : prev);
  }, []);

  const items = (data?.items || []).filter(i => cat === 'All' || i.category === cat);

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="m-approvals-page">
      {/* Header */}
      <div className="bg-[#1F7A6E] text-white px-4 pt-4 pb-5">
        <p className="text-[10px] font-bold uppercase tracking-widest text-white/60">Overview</p>
        <div className="flex items-center justify-between mt-0.5">
          <h1 className="font-display font-bold text-xl">
            Approvals
            {(data?.total || 0) > 0 && (
              <span className="ml-2 bg-rose-500 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-testid="m-approvals-badge">
                {data.total}
              </span>
            )}
          </h1>
          <button onClick={load}
            className="flex items-center gap-1 text-xs text-white/70 active:text-white"
            data-testid="m-approvals-refresh">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Category filter — horizontal scroll */}
      <div className="flex gap-2 px-4 py-3 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
        {CATEGORIES.map(c => {
          const count = c === 'All' ? (data?.total || 0) : (data?.by_category?.[c] || 0);
          if (c !== 'All' && count === 0) return null;
          return (
            <button key={c} onClick={() => setCat(c)}
              data-testid={`m-approvals-filter-${c.toLowerCase()}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-display font-bold whitespace-nowrap transition-colors ${
                cat === c ? 'bg-[#1F7A6E] text-white' : 'bg-white text-slate-600 border border-slate-200'
              }`}>
              {c}
              {count > 0 && (
                <span className={`text-[10px] font-bold px-1 rounded-full ${
                  cat === c ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
          <RefreshCw size={16} className="animate-spin mr-2" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center py-16 text-slate-400" data-testid="m-approvals-empty">
          <Inbox size={36} className="mb-3 text-slate-300" />
          <p className="font-display font-bold text-slate-500 text-sm">All clear</p>
          <p className="text-xs mt-1">Nothing pending your approval.</p>
        </div>
      ) : (
        <div className="px-4 space-y-3" data-testid="m-approvals-list">
          {items.map(item => (
            <ApprovalCard key={item.id} item={item} onAction={onAction} />
          ))}
        </div>
      )}
    </div>
  );
}
